import React, { useState, useEffect } from 'react';
import { X, Search, Plus } from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const CustomerSelectionModal = ({ 
  onClose, 
  onCustomerSelected, 
  customers = [], 
  selectedCustomerId = null 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    alternate_mobile: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.mobile.includes(searchTerm) ||
    (customer.alternate_mobile && customer.alternate_mobile.includes(searchTerm))
  );

  const handleCustomerSelect = (customer) => {
    onCustomerSelected(customer);
    onClose();
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    // Validate mobile numbers
    if (formData.mobile.length !== 10) {
      toast.error('Primary mobile number must be exactly 10 digits');
      return;
    }
    
    if (formData.alternate_mobile && formData.alternate_mobile.length !== 10) {
      toast.error('Alternate mobile number must be exactly 10 digits');
      return;
    }
    
    setLoading(true);

    try {
      const customerData = {
        id: `cust-${Date.now()}`,
        ...formData,
        created_at: new Date().toISOString()
      };
      
      const res = await axiosInstance.post('/customers', customerData);
      toast.success('Customer added successfully!');
      onCustomerSelected(res.data);
      onClose();
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('duplicate')) {
        toast.error('Customer with this mobile number already exists!');
      } else {
        toast.error(err.response?.data?.detail || 'Error adding customer');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-4 flex justify-between items-center border-b-2 border-amber-200">
          <h2 className="text-lg font-bold text-gray-800">
            {showAddForm ? 'Add New Customer' : 'Select Customer'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!showAddForm ? (
            <>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  placeholder="Search by name or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Add New Button */}
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full mb-4 py-2 px-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add New Customer
              </button>

              {/* Customer List */}
              <div className="space-y-2">
                {filteredCustomers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm ? 'No customers found' : 'No customers available'}
                  </div>
                ) : (
                  filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                        selectedCustomerId === customer.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                      }`}
                    >
                      <div className="font-semibold text-sm text-gray-800">{customer.name}</div>
                      <div className="text-xs text-gray-600">{customer.mobile}</div>
                      {customer.address && (
                        <div className="text-xs text-gray-500 mt-1">{customer.address}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* Add Customer Form */
            <form onSubmit={handleAddCustomer} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile *</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  required
                  className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  value={formData.mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, mobile: value });
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const value = pastedText.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, mobile: value });
                  }}
                  minLength={10}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                />
                {formData.mobile && formData.mobile.length !== 10 && (
                  <p className="text-xs text-red-500 mt-1">Must be exactly 10 digits ({formData.mobile.length}/10)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Alternate Mobile</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  value={formData.alternate_mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, alternate_mobile: value });
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const value = pastedText.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, alternate_mobile: value });
                  }}
                  minLength={10}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="10-digit alternate mobile"
                />
                {formData.alternate_mobile && formData.alternate_mobile.length > 0 && formData.alternate_mobile.length !== 10 && (
                  <p className="text-xs text-red-500 mt-1">Must be exactly 10 digits ({formData.alternate_mobile.length}/10)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
                <textarea
                  className="w-full px-3 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none text-sm"
                  rows="3"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter address"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 py-2 px-4 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2 px-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Adding...' : 'Add Customer'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerSelectionModal;